// =====================================================================================================================
// THE APPLICATION (APP)
//
//  App startup sequence:
//	    index.html --> AppLaunch.ts --> App.ts --> First Page
//
//

import { DataManager } from "@stratisopt/platform";
import { Ui } from "@stratisopt/platform";
import { UiFactory } from "@stratisopt/platform";
import { UiSet, UiSearchType } from "@stratisopt/platform";
import { Effect } from "@stratisopt/platform";
import { MessageView } from "@stratisopt/platform";
import { Action } from "@stratisopt/platform";
import { EventManager } from "@stratisopt/platform";
import { Globals } from "./Global";
import { HomeView } from "./Views/HomeView";
import { ProjectsView } from "./Views/ProjectsView";
import { ResumeView } from "./Views/ResumeView";
import { LogoView } from "./Views/LogoView";
import { NavItem } from "./Views/NavItem";
import { ProjectArea } from "./Views/ProjectArea";
import { ButtonView } from "./Views/ButtonView";
import { Navigation } from "@stratisopt/platform";


enum Actions {
    SHOW = "SHOW",                  // Shows a sub-view
    SHOWPRIOR = "SHOWPRIOR",        // Shows the prior sub-view
    SHOWNEXT = "SHOWNEXT",          // Shows the next sub-view
    SHOWMODAL = "SHOWMODAL",        // Shows view as a modal
    SHOWMESSAGE = "SHOWMESSAGE",    // Shows a modal message
}

enum AppUITypes {
    HOME_VIEW = "HOME_VIEW",
    PROJECTS_VIEW = "PROJECTS_VIEW",
    RESUME_VIEW = "RESUME_VIEW",

    LOGO_VIEW = "LOGO_VIEW",
    NAV_ITEM = "NAV_ITEM",
    PROJECT_AREA = "PROJECT_AREA",
    BUTTON_VIEW = "BUTTON_VIEW"
}


export class App {

    // =================================================================================================================
    // FIELDS
    //

    // Static
    public static _app: App;                                            // The app
    private static _dataManager:DataManager;                            // Data Manager

    // Instance
    private _views:UiSet;                                               // App views
    private _rootView:Ui;                                               // The root view (may be the same as the active view)
    private _activeView:Ui;                                             // Active view (may be a subview)


    // =================================================================================================================
    // CONSTRUCTORS
    //
    //  #region
    //
    //	Creates an instance of the app
    //	Initializes the app
    //	Shows the startUI


    // -----------------------------------------------------------------------------------------------------------------
    // STATIC ENTRY POINT
    //
    static async initialize() {
        // Initialize statics
        EventManager.initialize();
        Action.initialize(this.execute);                                // Initialize action with app's executor
        Ui.initialize(Globals.uiTheme);                                 // Initialize UI with the app's theme
        Ui.loadFont({family:'Lato', weights:[400,700]});
        Effect.initialize();
        UiFactory.initialize(this.getUi);                               // Initialize the UiFactory with the app's ui factory

        EventManager.publish(this.constructor.name, "-----------------------------------------------------------------------------------");
        EventManager.publish(this.constructor.name, "Initializing");

        this._dataManager = new DataManager();                          // Create the data manager
        await this._dataManager.initialize();                           // No datasets yet - pass them here when this app has data

        this._app = new App();                                          // Create the app
        Navigation.enableBrowserHistory();

        EventManager.publish(this.constructor.name, "Initialization completed");
    }

    // -----------------------------------------------------------------------------------------------------------------
    // APP CONSTRUCTOR
    //
    constructor() {
        App._app = this;            // Set this now to allow initializations to see this instance with static methods
        this._views = new UiSet();
        this.initViews();
        this.show();                // Show what needs to be shown in the way it needs to be shown
    }

    // -----------------------------------------------------------------------------------------------------------------
    // INIT VIEWS
    //
    private initViews() {
        this._views.add(HomeView.create());
        this._views.add(ProjectsView.create());
        this._views.add(ResumeView.create());
    }

    // -----------------------------------------------------------------------------------------------------------------
    // SHOW
    //
    private show() {

        // Cold load: if the URL names a page (by publicName), show that page so shared links deep-link
        // straight to it. Otherwise fall back to the home view.
        const route:string[] = Navigation.currentRoute();
        if (route.length > 0) {
            const view:Ui = this._views.findByPublicName(route[0]);
            if (view !== undefined) {
                this.showView(view);
                // Restore the in-page tab path (e.g. /Projects/PandaMug) by selecting each segment.
                if (route.length > 1) {
                    Navigation.resolvePath(view, route.slice(1));
                }
                return;
            }
        }

        this.showViewByName("homeView");
    }


    // #endregion


    // =================================================================================================================
    // SHOWING
    //
    // #region

    // -----------------------------------------------------------------------------------------------------------------
    // SET THE ACTIVE VIEW BY NAME
    //
    public showViewByName(name:string) {

        let found = this._views.findByName(name);
        if (found === undefined) {
            EventManager.publish(this.constructor.name, "Cannot find View: " + name);
            return;
        }
        this.showView(found);
    }

    // -----------------------------------------------------------------------------------------------------------------
    // SHOWS A VIEW OR A SUBVIEW
    //
    //  If the view does not have the subview attribute set:
    //      view becomes the root
    //
    //  If the view has subview attribute set:
    //      view is passed to the root if it exists
    //      if no root, subview becomes the root
    //
    public showView(view:Ui) {

        if (view === undefined) return;

        EventManager.publish(this.constructor.name, "Showing " + view.nameExtended);

        if (view.data.subview === undefined || this._rootView === undefined) {
            // Not flagged as a subview, or no root view - just display
            if (view.showAsPage(view.nameExtended) == false) {
                EventManager.publish(this.constructor.name, "Cannot show view: " + view.name);
                return;
            }
            this._rootView = view;
        } else {
            // Flagged as a subview and root exists - pass it to the root
            this._rootView.showSubView(view);
        }

        this._activeView = view;
    }

    // -----------------------------------------------------------------------------------------------------------------
    // SHOW MODAL BY NAME
    //
    //  Shows the subview as a modal
    //
    public showModalByName(name:string) {
        let ui:Ui = this._views.findByName(name);
        if (ui === undefined) return;
        ui.showAsModal();
    }

    // -----------------------------------------------------------------------------------------------------------------
    // FIND UI BY NAME IN ACTIVE UI
    //
    public static findInRootUiByName(name:string):Ui {
        return this._app._rootView.childFindByName(name);
    }

    // #endregion


    // =================================================================================================================
    // ACCESSORS
    //
    // #region

    public static getDataManager() {return this._dataManager;}

    // -----------------------------------------------------------------------------------------------------------------
    // RETURN ANY UI BY NAME
    //
    // Searches current and cached Uis
    //
    public static ui(name:string):Ui {
        return this._app._views.findByName(name, UiSearchType.LEAFWARD);       // Search all the subviews
    }

    public static uiByValue(name:string):Ui {
        return this._app._views.findByValue(name, UiSearchType.LEAFWARD);      // Search all the subviews
    }

    // #endregion


    // =================================================================================================================
    // ACTION EXECUTION
    //
    // #region

    public static async execute(action: Action) {

        switch (action.method) {
            case Actions.SHOW:          return App._app.executeShow(action);
            case Actions.SHOWPRIOR:     return App._app.executeShowPrior(action);
            case Actions.SHOWNEXT:      return App._app.executeShowNext(action);
            case Actions.SHOWMODAL:     return App._app.executeShowModal(action);
            case Actions.SHOWMESSAGE:   return App._app.executeShowMessage(action);
        }

    }

    // -----------------------------------------------------------------------------------------------------------------
    // SHOW
    //
    // DESCRIPTION:		Show a subview
    // SYNTAX:			Show
    //
    public executeShow(action: Action) {this.showViewByName(action.args[0]); return true;}

    // -----------------------------------------------------------------------------------------------------------------
    // SHOW PRIOR SUBVIEW
    //
    // DESCRIPTION:		Shows the prior subview
    // SYNTAX:			ShowPrior
    //
    public async executeShowPrior(action:Action): Promise<boolean> {
        history.back();
        return true;
    }

    // -----------------------------------------------------------------------------------------------------------------
    // SHOW NEXT SUBVIEW
    //
    // DESCRIPTION:		Shows the next subview
    // SYNTAX:			ShowNext
    //
    public async executeShowNext(action:Action): Promise<boolean> {
        history.forward();
        return true;
    }

    // -----------------------------------------------------------------------------------------------------------------
    // SHOW MODAL VIEW
    //
    // DESCRIPTION:		Shows a view as a modal
    // SYNTAX:			ShowModal <view name>
    //
    public async executeShowModal(action:Action): Promise<boolean> {
        this.showModalByName(action.args[0]);
        return true;
    }

    // -----------------------------------------------------------------------------------------------------------------
    // SHOW MESSAGE
    //
    // DESCRIPTION:		Shows a modal message
    // SYNTAX:			ShowMessage <message> <button text> <image name>
    //
    public async executeShowMessage(action:Action): Promise<boolean> {
        let messageView:Ui = MessageView.create(action.args[0],action.args[1],action.args[2]);
        messageView.showAsModal();
        return true;
    }

    // #endregion


    // =================================================================================================================
    // STATIC COMMANDS
    //
    // #region
    //

    // Show a UI
    public static show(ui:Ui) {
        this._app.showView(ui);
    }

    // Show prior UI
    public static showPrior() {
        history.back();
    }

    // Show a popup message
    public static showMessage(message:string, buttons:string = undefined, image:string = undefined, header:string = undefined, footer:string = undefined, textSize:string = undefined, viewSize:string = undefined) {
        let messageView:Ui = MessageView.create(message, buttons, image, header, footer, textSize, viewSize);
        messageView.showAsModal();
    }

    //  #endregion


    // =================================================================================================================
    // APP UI FACTORY
    //
    // #region
    //  Used to make App ui's accessible via JSON
    //  Creates a new Ui instance when a type name is used
    //  Returns an existing cached Ui instance when a ui name is used

    // -----------------------------------------------------------------------------------------------------------------
    // Get UI - but not the children
    //
    public static getUi(type: string, json: any): Ui {

        switch (type.toUpperCase()) {

            case AppUITypes.HOME_VIEW:          return HomeView.create();
            case AppUITypes.PROJECTS_VIEW:      return ProjectsView.create();
            case AppUITypes.RESUME_VIEW:        return ResumeView.create();
            case AppUITypes.LOGO_VIEW:          return LogoView.create(json);
            case AppUITypes.NAV_ITEM:           return NavItem.create(json);
            case AppUITypes.PROJECT_AREA:       return ProjectArea.create(json);
            case AppUITypes.BUTTON_VIEW:        return ButtonView.create(json);

            default:
                // Not found. Look for an existing ui instance by name using the type as the name
                let ui:Ui = App._app._views.findByName(type);
                if (ui !== undefined) return ui;
        }

        return undefined;

    }

    //  #endregion

}
